// Admin: Quan ly 7 bo cau hoi - PRD §6.1.B R-P0-B3, B7, B8, B9, B10
// Tinh nang M3.4 (trong file nay):
//   - 7 tabs (7 bo), badge so cau moi bo
//   - Search FTS5 trong bo dang chon
//   - Filter theo topic (dropdown tu /question-topics/:bo)
//   - List cau hoi co phan trang (limit 50)
//   - Universal actions / row: Edit / Delete / Chuyen bo
//   - cho_duyet: them shortcut Approve / Approve-Edit / Reject
//   - Nut "Them cau hoi" tao moi o bo dang chon
// Import/Export + Topic DnD se o M3.5 (file QuestionsToolbar sau).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { BO_ENUM, BO_META, boLabel, BO_THUONG } from '../lib/bo.js';
import { showConfirm, showPrompt } from '../lib/dialogs.js';
import { renderRichText, stripHtml } from '../lib/renderRichText.js';
import QuestionEditor from '../components/QuestionEditor.jsx';
import AdminPageHeader from '../components/AdminPageHeader.jsx';
import MoveBoDialog from '../components/MoveBoDialog.jsx';
import ImportExcelDialog from '../components/ImportExcelDialog.jsx';
import ExportExcelDialog from '../components/ExportExcelDialog.jsx';
import TopicOrderDialog from '../components/TopicOrderDialog.jsx';

// Bo co toolbar Import/Export Excel + Sap xep chu de
// PRD R-P0-B4 (3 bo thuong). M52 (2026-04-25): gỡ bo_noi_quy vì đã tách
// sang tính năng Nội quy riêng (/noi-quy + /admin/rules-modules).
const BO_WITH_TOOLBAR = new Set(['bo_1000k', 'bo_1250k', 'bo_nang_cao']);
// M155 (2026-05-13): Import/Export mở rộng cho 2 bộ AI — admin backup + share cross-shop.
// Export yêu cầu TOTP admin (giữ nguyên ExportExcelDialog flow).
const BO_WITH_IMPORT_EXPORT = new Set(['bo_1000k', 'bo_1250k', 'bo_nang_cao', 'bo_ai_da_sua', 'bo_ai_ngon']);

const PAGE_SIZE = 50;

export default function QuestionsPage() {
  // M156 (2026-05-13): Giữ tab bộ câu hỏi qua F5 / share link bằng URL query ?bo=...
  // Trước đây sau F5 luôn nhảy về "Chờ duyệt" (UX khó chịu khi đang sửa 1 bộ khác).
  const [searchParams, setSearchParams] = useSearchParams();
  const _urlBo = searchParams.get('bo');
  const [activeBo, setActiveBo] = useState(
    BO_ENUM.includes(_urlBo) ? _urlBo : 'cho_duyet'
  );
  const [search, setSearch] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [offset, setOffset] = useState(0);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // counts theo bo (badge tab)
  const [counts, setCounts] = useState({});
  // topics cua bo dang chon (filter dropdown)
  const [topics, setTopics] = useState([]);

  // editor + move dialog state
  const [editor, setEditor] = useState({ open: false, mode: 'create', initial: null });
  const [moveDlg, setMoveDlg] = useState({ open: false, question: null });
  // M3.5 - toolbar dialogs
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [topicOrderOpen, setTopicOrderOpen] = useState(false);
  // M14 — bulk delete dialog: null | { scope: 'bo'|'all', bo: string|null, count: number }
  const [bulkDelete, setBulkDelete] = useState(null);
  // M23 (2026-04-24) — badge cảnh báo câu thiếu embedding (auto-embed fail)
  // null = chưa load; { count, per_bo, provider_status } sau khi load
  const [missingEmbed, setMissingEmbed] = useState(null);
  const [retrying, setRetrying] = useState(false);
  // M29.A (2026-04-24) — Merge câu trùng (R-P0-B15):
  //   mergeMode: true khi admin đang chọn 2 câu để gộp
  //   selectedForMerge: Array<question_id> (max 2)
  //   mergeDialog: { q1, q2 } khi đủ 2 câu → modal chọn câu giữ
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [mergeDialog, setMergeDialog] = useState(null);
  // M29.A2 (2026-04-24) — AI quét cặp câu trùng (cosine ≥ 0.88)
  const [scanDialog, setScanDialog] = useState(null);  // { loading, pairs, threshold }
  // M30.A (2026-04-24) — AI generate câu hỏi mới (B4a)
  const [generateOpen, setGenerateOpen] = useState(false);
  // 2026-04-24 — Bulk select (Set<id>) + Move-bulk dialog (chọn target_bo)
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Debounce search
  const searchTimer = useRef(null);
  function onSearchChange(v) {
    setSearch(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setOffset(0);
      reload({ q: v.trim(), bo: activeBo, topic: topicFilter, offset: 0 });
    }, 300);
  }

  async function reload(overrides = {}) {
    const params = {
      bo: overrides.bo ?? activeBo,
      topic: overrides.topic ?? topicFilter,
      q: overrides.q ?? search.trim(),
      limit: PAGE_SIZE,
      offset: overrides.offset ?? offset,
    };
    setLoading(true);
    try {
      const res = await api.questionsList(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Lỗi tải danh sách');
    } finally {
      setLoading(false);
    }
  }

  async function reloadTopics(bo) {
    try {
      const res = await api.topicsList(bo);
      setTopics(res.topics || []);
    } catch (err) {
      setTopics([]);
    }
  }

  async function reloadCounts() {
    try {
      const results = await Promise.all(
        BO_ENUM.map((b) => api.questionsList({ bo: b, limit: 1 }).then((r) => [b, r.total]).catch(() => [b, 0]))
      );
      const map = {};
      for (const [b, c] of results) map[b] = c;
      setCounts(map);
    } catch { /* ignore */ }
  }

  // M23 — load số câu thiếu embedding (cho badge cảnh báo)
  async function reloadMissingEmbed() {
    try {
      const r = await api.adminQuestionsMissingEmbedCount();
      setMissingEmbed(r);
    } catch {
      // Không phải admin hoặc API lỗi → ẩn badge
      setMissingEmbed(null);
    }
  }

  // M23 — retry embed các câu thiếu (user bấm nút trên badge)
  async function onRetryMissingEmbed() {
    const n = missingEmbed?.count ?? 0;
    if (n === 0) return;
    const ok = await showConfirm({
      title: 'Embed lại câu thiếu',
      message: `Thử embed lại ${n} câu chưa embed?\nChi phí ước tính: vài trăm đồng (OpenAI text-embedding-3-small).`,
      confirmText: 'Embed',
    });
    if (!ok) return;
    setRetrying(true);
    try {
      const res = await api.adminQuestionsRetryMissingEmbed();
      toast.success(
        `Đã embed ${res.embedded ?? 0}/${res.scanned ?? 0} câu` +
        (res.cost_vnd ? ` · ~${Math.round(res.cost_vnd)}đ` : '') +
        (res.failed ? ` · ${res.failed} lỗi` : '')
      );
      reloadMissingEmbed();
    } catch (err) {
      const head = err?.data?.error || err?.message || 'Retry lỗi';
      toast.error(head);
    } finally {
      setRetrying(false);
    }
  }

  useEffect(() => { reloadCounts(); reloadMissingEmbed(); }, []);
  useEffect(() => {
    setTopicFilter('');
    setOffset(0);
    reload({ bo: activeBo, topic: '', offset: 0 });
    reloadTopics(activeBo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBo]);
  // M156 — Sync activeBo lên URL (?bo=...) để F5 giữ tab. Dùng replace để không stack history.
  // Default 'cho_duyet' → bỏ query param cho URL sạch.
  useEffect(() => {
    const current = searchParams.get('bo');
    if (activeBo === 'cho_duyet') {
      if (current) {
        const next = new URLSearchParams(searchParams);
        next.delete('bo');
        setSearchParams(next, { replace: true });
      }
    } else if (current !== activeBo) {
      const next = new URLSearchParams(searchParams);
      next.set('bo', activeBo);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBo]);
  useEffect(() => {
    reload({ topic: topicFilter, offset });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicFilter, offset]);

  // M29.A — Merge toggle + select handlers
  function toggleMergeMode() {
    setMergeMode((m) => {
      if (m) setSelectedForMerge([]);  // exit merge mode → clear selection
      return !m;
    });
  }

  function toggleSelectForMerge(id) {
    setSelectedForMerge((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) {
        toast('Chỉ chọn tối đa 2 câu để gộp');
        return prev;
      }
      return [...prev, id];
    });
  }

  // 2026-04-24 — Bulk select handlers
  function toggleBulkSelect(id) {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function clearBulkSelect() { setBulkSelected(new Set()); }
  function selectAllInPage() {
    setBulkSelected(new Set(items.map((q) => q.id)));
  }
  function isAllPageSelected() {
    return items.length > 0 && items.every((q) => bulkSelected.has(q.id));
  }

  // Bulk action handlers
  async function runBulk(action, target_bo = null) {
    if (bulkBusy) return;
    const ids = [...bulkSelected];
    if (ids.length === 0) {
      toast('Chưa chọn câu nào');
      return;
    }
    // Confirm
    const labels = { approve: 'Duyệt', reject: 'Từ chối (xoá)', delete: 'Xoá vĩnh viễn', move_bo: `Chuyển sang ${boLabel(target_bo)}` };
    const okBulk = await showConfirm({
      title: `${labels[action]} ${ids.length} câu`,
      message: `${labels[action]} ${ids.length} câu đã chọn?\n\nThao tác KHÔNG hoàn tác được.`,
      confirmText: labels[action],
      dangerous: action === 'delete' || action === 'reject',
    });
    if (!okBulk) return;

    setBulkBusy(true);
    try {
      const res = await api.questionsBulkAction({ ids, action, target_bo: target_bo || undefined });
      const skip = res.skip_reason ? ` (${res.skip_reason})` : '';
      const nf = res.not_found_count > 0 ? ` · ${res.not_found_count} không tìm thấy` : '';
      toast.success(`Đã ${labels[action].toLowerCase()} ${res.processed_count}/${res.requested_count} câu${skip}${nf}`);
      clearBulkSelect();
      setBulkMoveOpen(false);
      reload();
      reloadCounts();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Bulk action thất bại');
    } finally {
      setBulkBusy(false);
    }
  }

  // Auto-clear bulk selection khi đổi bộ / đổi search / đổi page
  useEffect(() => { clearBulkSelect(); }, [activeBo, topicFilter, search, offset]);

  function openMergeDialog() {
    if (selectedForMerge.length !== 2) return;
    const q1 = items.find((x) => x.id === selectedForMerge[0]);
    const q2 = items.find((x) => x.id === selectedForMerge[1]);
    if (!q1 || !q2) {
      toast.error('Không tìm thấy câu đã chọn — refresh trang và thử lại');
      return;
    }
    setMergeDialog({ q1, q2 });
  }

  // M29.A2 — quét AI cặp câu trùng trong bộ đang active
  // M120 (2026-04-27): cho_duyet ok luôn — BE embed transient on-demand,
  // không lưu DB. Chi phí ~1đ/câu (1 lần / lần admin scan).
  async function openScanDuplicates() {
    setScanDialog({ loading: true, pairs: [], threshold: 0.88 });
    try {
      const res = await api.questionsDuplicates({ bo: activeBo, threshold: 0.88, limit: 30 });
      setScanDialog({
        loading: false,
        pairs: res.pairs || [],
        total_scanned: res.total_scanned,
        total_pairs_found: res.total_pairs_found,
        threshold: res.threshold,
        note: res.note,
        // M120: cost info nếu BE embed on-demand
        embed_cost_vnd: res.embed_cost_vnd || 0,
        embed_count: res.embed_count || 0,
      });
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Quét thất bại');
      setScanDialog(null);
    }
  }

  // Khi admin click 1 pair trong ScanDialog → đóng scan + mở MergeDialog với 2 câu đó
  function pickPairForMerge(pair) {
    setScanDialog(null);
    setSelectedForMerge([pair.q1.id, pair.q2.id]);
    // Cần load 2 câu full (pair chỉ có subset fields) — dùng trực tiếp data pair
    setMergeDialog({
      q1: { ...pair.q1, bo: activeBo },
      q2: { ...pair.q2, bo: activeBo },
    });
  }

  async function confirmMerge(keepId, deleteId) {
    try {
      await api.questionDelete(deleteId);
      toast.success(`Đã gộp — giữ câu #${keepId}, xoá câu #${deleteId}`);
      setMergeDialog(null);
      setSelectedForMerge([]);
      setMergeMode(false);
      await reload();
      await reloadCounts();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Gộp thất bại');
    }
  }

  function openCreate() {
    setEditor({ open: true, mode: 'create', initial: null });
  }
  function openEdit(q) {
    setEditor({ open: true, mode: 'edit', initial: q });
  }
  function openApproveEdit(q) {
    setEditor({ open: true, mode: 'approve-edit', initial: q });
  }

  async function handleSave(payload) {
    const { mode, initial } = editor;
    try {
      if (mode === 'create') {
        await api.questionCreate({
          bo: payload.bo,
          question: payload.question,
          answer: payload.answer,
          topic: payload.topic,
          source: 'manual',
        });
        toast.success('Đã thêm câu hỏi');
      } else if (mode === 'edit') {
        await api.questionUpdate(initial.id, {
          question: payload.question,
          answer: payload.answer,
          topic: payload.topic,
        });
        toast.success('Đã lưu');
      } else if (mode === 'approve-edit') {
        await api.questionApproveEdit(initial.id, {
          question: payload.question,
          answer: payload.answer,
          topic: payload.topic,
        });
        toast.success('Đã duyệt + chuyển sang bộ AI đã sửa');
      }
    } catch (err) {
      toast.error(err?.data?.error || err?.message);
      throw err;
    }
    setEditor({ open: false, mode: 'create', initial: null });
    reload();
    reloadCounts();
    reloadTopics(activeBo);
  }

  async function handleDelete(q) {
    const preview = q.question.length > 80 ? q.question.slice(0, 80) + '...' : q.question;
    const okDel = await showConfirm({
      title: 'Xoá vĩnh viễn câu hỏi',
      message: `Xoá vĩnh viễn câu hỏi này?\n\n"${preview}"`,
      confirmText: 'Xoá',
      dangerous: true,
    });
    if (!okDel) return;
    try {
      await api.questionDelete(q.id);
      toast.success('Đã xoá');
      reload();
      reloadCounts();
    } catch (err) {
      toast.error(err?.data?.error || err?.message);
    }
  }

  async function handleMoveBo(target) {
    const q = moveDlg.question;
    try {
      await api.questionMoveBo(q.id, target);
      toast.success(`Đã chuyển sang ${boLabel(target)}`);
      setMoveDlg({ open: false, question: null });
      reload();
      reloadCounts();
    } catch (err) {
      toast.error(err?.data?.error || err?.message);
      throw err;
    }
  }

  async function handleApprove(q) {
    try {
      await api.questionApprove(q.id);
      toast.success('Đã duyệt → bộ AI ngon');
      reload();
      reloadCounts();
    } catch (err) {
      toast.error(err?.data?.error || err?.message);
    }
  }

  async function handleReject(q) {
    const reason = await showPrompt({
      title: 'Từ chối câu hỏi',
      message: 'Lý do từ chối (tuỳ chọn):',
      placeholder: 'Nhập lý do hoặc để trống…',
      multiline: true,
      confirmText: 'Từ chối',
    });
    if (reason === null) return;
    try {
      await api.questionReject(q.id, reason);
      toast.success('Đã từ chối (xoá)');
      reload();
      reloadCounts();
    } catch (err) {
      toast.error(err?.data?.error || err?.message);
    }
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  // M147 (2026-05-12): Header 2-row chuẩn — back link trên, title 2xl, actions phải.
  const questionActions = (
    <>
      {missingEmbed && missingEmbed.count > 0 && (
        <button
          onClick={onRetryMissingEmbed}
          disabled={retrying}
          title={
            missingEmbed.provider_status?.ready
              ? `Có ${missingEmbed.count} câu chưa embed — bấm để retry`
              : (missingEmbed.provider_status?.reason || 'Provider embedding chưa sẵn sàng')
          }
          className="text-xs px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 hover:bg-amber-200 disabled:opacity-50 disabled:cursor-wait"
        >
          {retrying
            ? '⏳ Đang embed lại…'
            : `⚠️ ${missingEmbed.count} câu chưa embed — retry`}
        </button>
      )}
      <button onClick={openCreate}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 shadow-sm">
        <span>＋</span><span>Thêm câu</span>
      </button>
      <button
        onClick={() => setGenerateOpen(true)}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-sm"
        title="AI tự sinh 5/10/20 câu hỏi mới dựa trên chủ đề + kho hiện tại"
      >
        <span>🪄</span><span>AI tạo</span>
      </button>
      <button
        onClick={toggleMergeMode}
        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border shadow-sm ${
          mergeMode
            ? 'bg-rose-100 border-rose-300 text-rose-700 hover:bg-rose-200'
            : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
        }`}
        title="Gộp 2 câu trùng lặp — chọn 2 câu → chọn câu giữ → câu còn lại bị xoá"
      >
        {mergeMode ? <><span>✕</span><span>Thoát gộp</span></> : <><span>🔀</span><span>Gộp trùng</span></>}
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 md:px-5 pt-4">
        <AdminPageHeader
          title="Câu hỏi (6 bộ)"
          subtitle="Quản lý câu hỏi — tạo thủ công, AI sinh, gộp trùng"
          actions={questionActions}
        />
      </div>
      <div className="bg-white border-b border-t">
        {/* M29.A — Merge mode banner */}
        {mergeMode && (
          <div className="max-w-7xl mx-auto px-4 md:px-5 pb-2">
            <div className="flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm flex-wrap">
              <span className="text-rose-700 font-medium">
                🔀 Chế độ gộp: đã chọn {selectedForMerge.length}/2 câu
              </span>
              <span className="text-slate-600 text-xs">
                AI quét tự động hoặc tick tay 2 câu → chọn câu giữ
              </span>
              <div className="flex-1" />
              {/* M29.A2 + M120 — Nút quét AI (cho_duyet được phép, embed on-demand) */}
              <button
                onClick={openScanDuplicates}
                className="px-3 py-1.5 bg-amber-500 text-white rounded text-sm font-medium hover:bg-amber-600"
                title={activeBo === 'cho_duyet'
                  ? 'Chờ duyệt chưa lưu embed — sẽ embed tạm để quét (~1đ/câu)'
                  : 'AI quét cặp cosine ≥ 0.88 trong bộ hiện tại'}
              >
                🔍 Quét AI cặp trùng
              </button>
              <button
                onClick={openMergeDialog}
                disabled={selectedForMerge.length !== 2}
                className="px-3 py-1.5 bg-rose-600 text-white rounded text-sm font-medium hover:bg-rose-700 disabled:opacity-40"
              >
                Gộp 2 câu đã chọn →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* M56 (2026-04-25): sticky header — tabs + toolbar + search đứng cố định khi scroll list dài.
          Gom 4 phần (tabs / excel toolbar / bulk delete / search+filter) vào 1 container sticky. */}
      <div className="sticky top-0 z-30 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-5 pt-4">
          <div className="flex flex-wrap gap-1 border-b">
            {BO_ENUM.map((bo) => {
              const meta = BO_META[bo];
              const isActive = bo === activeBo;
              const count = counts[bo] ?? '...';
              const badgeClass =
                bo === 'cho_duyet' && count > 20 ? 'bg-red-100 text-red-700' :
                isActive ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600';
              return (
                <button key={bo} onClick={() => setActiveBo(bo)}
                        className={`px-3 py-2 text-sm border-b-2 -mb-px transition flex items-center gap-2 ${
                          isActive ? 'border-emerald-600 text-emerald-700 font-medium' : 'border-transparent text-slate-600 hover:text-slate-900'
                        }`}>
                  <span>{meta.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${badgeClass}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* M59 (2026-04-25): gộp 2 toolbars (Excel + Xoá bộ) thành 1 hàng compact */}
        <div className="max-w-7xl mx-auto px-4 md:px-5 pt-3 flex flex-wrap items-center gap-2">
          {/* M155: Import/Export — mở rộng cho ai_da_sua + ai_ngon. Sort topic chỉ 3 bộ thưởng. */}
          {BO_WITH_IMPORT_EXPORT.has(activeBo) && (
            <>
              <button onClick={() => setImportOpen(true)}
                      title="Import Excel — yêu cầu TOTP admin"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-lg hover:bg-emerald-100">
                <span>📥</span><span>Import</span>
              </button>
              <button onClick={() => setExportOpen(true)}
                      title="Export Excel — yêu cầu TOTP admin"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-300 text-slate-800 rounded-lg hover:bg-slate-100">
                <span>📤</span><span>Export</span>
              </button>
            </>
          )}
          {BO_WITH_TOOLBAR.has(activeBo) && (
            <>
              <button onClick={() => setTopicOrderOpen(true)}
                      title="Sắp xếp thứ tự chủ đề trong bộ"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-100">
                <span>🎨</span><span>Sắp xếp chủ đề</span>
              </button>
              <span className="text-slate-300">|</span>
            </>
          )}
          {BO_WITH_IMPORT_EXPORT.has(activeBo) && !BO_WITH_TOOLBAR.has(activeBo) && (
            <span className="text-slate-300">|</span>
          )}
          <button
            onClick={() => setBulkDelete({ scope: 'bo', bo: activeBo, count: counts[activeBo] ?? 0 })}
            disabled={(counts[activeBo] ?? 0) === 0}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-rose-50 border border-rose-300 text-rose-800 rounded-lg hover:bg-rose-100 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Xoá tất cả câu hỏi trong bộ hiện tại — yêu cầu TOTP admin · KHÔNG hoàn tác (chạy `npm run backup` trước)"
            data-testid="bulk-delete-bo-btn"
          >
            <span>🗑</span><span>Xoá bộ</span>
            <span className="ml-0.5 px-1 rounded bg-rose-200/60 text-rose-900 font-bold">{counts[activeBo] ?? 0}</span>
          </button>
          <div className="ml-auto text-[11px] text-slate-400">
            🔒 Các tác vụ trên yêu cầu mã TOTP admin
          </div>
        </div>

        {/* M59 (2026-04-25): search có icon 🔍 + clear button khi có text */}
        <div className="max-w-7xl mx-auto px-4 md:px-5 py-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-64 max-w-lg">
            <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400 text-sm">🔍</span>
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Tìm câu hỏi hoặc đáp án..."
              className="w-full border border-slate-300 rounded-lg pl-9 pr-9 py-2 text-sm bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 focus:outline-none transition"
            />
            {search && (
              <button
                onClick={() => onSearchChange('')}
                title="Xoá tìm kiếm"
                className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-700 px-1"
              >
                ✕
              </button>
            )}
          </div>

          <select value={topicFilter} onChange={(e) => { setOffset(0); setTopicFilter(e.target.value); }}
                  className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm max-w-xs bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 focus:outline-none">
            <option value="">📁 Tất cả chủ đề</option>
            {topics.filter((t) => t.topic).map((t) => (
              <option key={t.topic} value={t.topic}>{t.topic} ({t.cnt})</option>
            ))}
            {topics.some((t) => !t.topic) && (
              <option value="__EMPTY__" disabled>(chưa phân chủ đề: {topics.find((t) => !t.topic).cnt})</option>
            )}
          </select>

          <div className="ml-auto text-sm text-slate-500 inline-flex items-center gap-1.5">
            {loading
              ? <><span className="animate-pulse">⏳</span><span>Đang tải…</span></>
              : <><span className="font-semibold text-slate-700">{total}</span><span>câu</span></>
            }
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-5 pb-32">
        {/* M59 (2026-04-25): "Chọn tất cả trang" — gọn hơn, để trên list (vì cần cho user biết) */}
        {!mergeMode && items.length > 0 && bulkSelected.size === 0 && (
          <div className="mb-2 flex items-center gap-2 text-xs">
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-slate-500 hover:text-slate-700 transition px-2 py-1 rounded hover:bg-slate-100">
              <input
                type="checkbox"
                checked={false}
                onChange={() => selectAllInPage()}
                className="w-3.5 h-3.5"
              />
              <span>Chọn tất cả {items.length} câu trang này</span>
            </label>
          </div>
        )}

        {/* M59: Bulk action bar — FLOATING sticky bottom khi có ≥1 câu đã chọn */}
        {!mergeMode && bulkSelected.size > 0 && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white shadow-2xl rounded-2xl border border-emerald-200 px-4 py-3 flex items-center gap-3 flex-wrap max-w-5xl">
            <span className="inline-flex items-center gap-2 text-sm">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs">
                {bulkSelected.size}
              </span>
              <span className="font-semibold text-slate-800">câu đã chọn</span>
            </span>
            <button
              onClick={clearBulkSelect}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
            >
              bỏ chọn
            </button>
            <label className="inline-flex items-center gap-1 cursor-pointer text-xs text-slate-500 hover:text-slate-700 ml-2">
              <input
                type="checkbox"
                checked={isAllPageSelected()}
                onChange={(e) => {
                  if (e.target.checked) selectAllInPage();
                  else clearBulkSelect();
                }}
                className="w-3.5 h-3.5"
              />
              <span>Chọn tất cả trang ({items.length})</span>
            </label>
            <span className="text-slate-300">|</span>
            {activeBo === 'cho_duyet' && (
              <>
                <button
                  disabled={bulkBusy}
                  onClick={() => runBulk('approve')}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold disabled:opacity-50 shadow-sm"
                >
                  ✓ Duyệt {bulkSelected.size}
                </button>
                <button
                  disabled={bulkBusy}
                  onClick={() => runBulk('reject')}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold disabled:opacity-50 shadow-sm"
                >
                  ✕ Từ chối {bulkSelected.size}
                </button>
                <span className="text-slate-300">|</span>
              </>
            )}
            <button
              disabled={bulkBusy}
              onClick={() => setBulkMoveOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-300 hover:bg-slate-100 rounded-lg font-semibold disabled:opacity-50"
            >
              📦 Chuyển bộ
            </button>
            <button
              disabled={bulkBusy}
              onClick={() => runBulk('delete')}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-rose-300 hover:bg-rose-50 text-rose-700 rounded-lg font-semibold disabled:opacity-50"
            >
              🗑 Xoá {bulkSelected.size}
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm divide-y">
          {items.length === 0 && !loading && (
            <div className="p-8 text-center text-slate-400 text-sm">
              Chưa có câu hỏi nào trong bộ này.
            </div>
          )}

          {items.map((q) => (
            <QuestionRow key={q.id}
              q={q}
              onEdit={() => openEdit(q)}
              onDelete={() => handleDelete(q)}
              onMoveBo={() => setMoveDlg({ open: true, question: q })}
              onApprove={() => handleApprove(q)}
              onApproveEdit={() => openApproveEdit(q)}
              onReject={() => handleReject(q)}
              mergeMode={mergeMode}
              mergeSelected={selectedForMerge.includes(q.id)}
              onToggleMergeSelect={() => toggleSelectForMerge(q.id)}
              bulkSelected={bulkSelected.has(q.id)}
              onToggleBulkSelect={() => toggleBulkSelect(q.id)}
              bulkActive={bulkSelected.size > 0 && !mergeMode}
            />
          ))}
        </div>

        {total > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm">
            <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}
                    className="px-3 py-1.5 border rounded disabled:opacity-40">&larr; Trước</button>
            <span className="text-slate-600">Trang {currentPage} / {pageCount}</span>
            <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total}
                    className="px-3 py-1.5 border rounded disabled:opacity-40">Sau &rarr;</button>
          </div>
        )}
      </div>

      <QuestionEditor
        open={editor.open}
        mode={editor.mode}
        initial={editor.initial}
        defaultBo={activeBo}
        onClose={() => setEditor({ open: false, mode: 'create', initial: null })}
        onSave={handleSave}
      />
      <MoveBoDialog
        open={moveDlg.open}
        question={moveDlg.question}
        onClose={() => setMoveDlg({ open: false, question: null })}
        onConfirm={handleMoveBo}
      />

      <ImportExcelDialog
        open={importOpen}
        bo={activeBo}
        onClose={() => setImportOpen(false)}
        onDone={() => { reload(); reloadCounts(); reloadTopics(activeBo); }}
      />
      <ExportExcelDialog
        open={exportOpen}
        bo={activeBo}
        onClose={() => setExportOpen(false)}
      />
      <TopicOrderDialog
        open={topicOrderOpen}
        bo={activeBo}
        onClose={() => setTopicOrderOpen(false)}
        onSaved={() => reloadTopics(activeBo)}
      />

      {/* 2026-04-24: Bulk move-bo dialog */}
      {bulkMoveOpen && (
        <BulkMoveDialog
          count={bulkSelected.size}
          currentBo={activeBo}
          busy={bulkBusy}
          onCancel={() => setBulkMoveOpen(false)}
          onConfirm={(target_bo) => runBulk('move_bo', target_bo)}
        />
      )}

      {/* M14 — Bulk delete modal */}
      {bulkDelete && (
        <BulkDeleteDialog
          scope={bulkDelete.scope}
          bo={bulkDelete.bo}
          count={bulkDelete.count}
          onClose={() => setBulkDelete(null)}
          onDone={() => {
            setBulkDelete(null);
            reload();
            reloadCounts();
            reloadTopics(activeBo);
          }}
        />
      )}

      {/* M29.A — Merge dialog (R-P0-B15) */}
      {mergeDialog && (
        <MergeDialog
          q1={mergeDialog.q1}
          q2={mergeDialog.q2}
          onCancel={() => setMergeDialog(null)}
          onConfirm={confirmMerge}
        />
      )}

      {/* M29.A2 — Duplicate scan dialog (R-P0-B4 AI kiểm tra trùng) */}
      {scanDialog && (
        <DuplicatesScanDialog
          bo={activeBo}
          data={scanDialog}
          onClose={() => setScanDialog(null)}
          onPickPair={pickPairForMerge}
        />
      )}

      {/* M30.A — AI generate dialog (R-P0-B4a) */}
      {generateOpen && (
        <AiGenerateDialog
          onClose={() => setGenerateOpen(false)}
          onGenerated={(res) => {
            setGenerateOpen(false);
            reload();
            reloadCounts();
            // Chuyển sang tab Chờ duyệt để admin review ngay
            setActiveBo('cho_duyet');
            toast.success(
              `🎉 Đã tạo ${res.inserted_count}/${res.generated_count} câu vào Chờ duyệt` +
              (res.duplicates_skipped ? ` (bỏ ${res.duplicates_skipped} trùng)` : '') +
              ` · ${res.cost.total_vnd}đ`,
              { duration: 6000 }
            );
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// M14 — BulkDeleteDialog: confirm + TOTP admin
// ============================================================
function BulkDeleteDialog({ scope, bo, count, onClose, onDone }) {
  const [totp, setTotp] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const requiredPhrase = `XOÁ ${boLabel(bo).toUpperCase()}`;

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (busy) return;
    if (!/^\d{6}$/.test(totp.trim())) {
      toast.error('Nhập đủ 6 số TOTP admin');
      return;
    }
    if (confirmText.trim() !== requiredPhrase) {
      toast.error(`Gõ chính xác: "${requiredPhrase}"`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.questionsBulkDelete({ bo, totpCode: totp.trim() });
      toast.success(`Đã xoá ${r.deleted_count} câu hỏi`);
      onDone();
    } catch (err) {
      const code = err?.data?.code;
      if (code === 'ADMIN_TOTP_REQUIRED' || code === 'ADMIN_TOTP_INVALID') {
        toast.error('Mã TOTP admin không đúng — thử lại');
        setTotp('');
      } else {
        const base = err?.data?.error || err?.message || 'Xoá thất bại';
        const detail = err?.data?.detail;
        toast.error(detail ? `${base}: ${detail}` : base, { duration: 8000 });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bulk-delete-dialog"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="text-3xl">⚠️</div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-rose-800">
              Xoá toàn bộ bộ "{boLabel(bo)}"
            </h2>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              Bạn sắp xoá <strong className="text-rose-700">{count}</strong> câu hỏi.
              <strong> Hành động KHÔNG thể hoàn tác.</strong>
              {' '}Nếu cần khôi phục, phải restore từ backup (xem <code>docs/RUNBOOK.md</code>).
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">
              Gõ chính xác: <code className="px-1 py-0.5 bg-slate-100 rounded text-rose-700">{requiredPhrase}</code>
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={requiredPhrase}
              className="mt-1 w-full border rounded px-3 py-2 text-sm font-mono"
              autoFocus
              data-testid="bulk-delete-confirm-text"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Mã TOTP admin (6 số)</label>
            <input
              type="text"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              maxLength={6}
              className="mt-1 w-full border rounded px-3 py-2 text-lg font-mono tracking-widest text-center"
              data-testid="bulk-delete-totp"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded disabled:opacity-40"
            >
              Huỷ
            </button>
            <button
              type="submit"
              disabled={busy || totp.length !== 6 || confirmText.trim() !== requiredPhrase}
              className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="bulk-delete-submit"
            >
              {busy ? 'Đang xoá…' : `Xoá ${count} câu`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// 2026-04-24: Modal chọn target_bo cho bulk move
function BulkMoveDialog({ count, currentBo, busy, onCancel, onConfirm }) {
  const [target, setTarget] = useState('');
  const candidates = BO_ENUM.filter((b) => b !== 'cho_duyet' && b !== currentBo);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
        <h3 className="text-lg font-semibold mb-2">📦 Chuyển {count} câu sang bộ khác</h3>
        <p className="text-sm text-slate-600 mb-3">
          Chọn bộ đích. Các câu đã ở đúng bộ sẽ tự bỏ qua.
        </p>
        <div className="space-y-1 mb-4 max-h-64 overflow-y-auto">
          {candidates.map((b) => (
            <label key={b} className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${target === b ? 'bg-brand-50 border-brand-300' : 'border-slate-200 hover:bg-slate-50'}`}>
              <input
                type="radio"
                name="target_bo"
                value={b}
                checked={target === b}
                onChange={(e) => setTarget(e.target.value)}
              />
              <span className="text-sm">{boLabel(b)}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-2 text-sm rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            Huỷ
          </button>
          <button
            onClick={() => target && onConfirm(target)}
            disabled={busy || !target}
            className="px-4 py-2 text-sm rounded bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50"
          >
            {busy ? 'Đang chuyển…' : `Chuyển ${count} câu`}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuestionRow({
  q, onEdit, onDelete, onMoveBo, onApprove, onApproveEdit, onReject,
  // M29.A merge mode
  mergeMode = false, mergeSelected = false, onToggleMergeSelect,
  // 2026-04-24 bulk select (luôn enabled khi !mergeMode)
  bulkSelected = false, onToggleBulkSelect, bulkActive = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const isChoduyet = q.bo === 'cho_duyet';

  // Highlight khi đang chọn cho bulk
  const bulkHighlight = !mergeMode && bulkSelected ? 'bg-brand-50 ring-1 ring-brand-300' : '';

  return (
    <div
      className={`p-4 transition ${
        mergeMode
          ? (mergeSelected ? 'bg-rose-50 ring-2 ring-rose-300' : 'hover:bg-rose-50/50 cursor-pointer')
          : `${bulkHighlight} hover:bg-slate-50`
      }`}
      onClick={mergeMode ? (e) => {
        const tag = e.target.tagName?.toLowerCase();
        if (tag === 'button' || tag === 'a' || tag === 'textarea' || tag === 'input') return;
        onToggleMergeSelect?.();
      } : undefined}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox bulk/merge select */}
        {mergeMode && (
          <input
            type="checkbox"
            checked={mergeSelected}
            onChange={() => onToggleMergeSelect?.()}
            className="mt-1 w-4 h-4 text-rose-600 rounded flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {!mergeMode && (
          <input
            type="checkbox"
            checked={bulkSelected}
            onChange={() => onToggleBulkSelect?.()}
            className="mt-1 w-4 h-4 text-brand-600 rounded flex-shrink-0 cursor-pointer"
            title="Chọn để bulk action"
          />
        )}

        <div className="flex-1 min-w-0">
          {/* M59 (2026-04-25): card header gọn — ID + topic badge + embed badge */}
          <div className="flex items-center gap-2 flex-wrap mb-1.5 text-[11px]">
            <span className="font-mono text-slate-400">#{q.id}</span>
            {q.topic && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                {q.topic}
              </span>
            )}
            {!isChoduyet && (
              q.has_embedding ? (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium"
                  title="Đã embed — sẵn sàng cho Hỏi đáp (RAG)"
                >
                  ✓ embed
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium"
                  title="Chưa embed — câu này sẽ không hiện trong kết quả Hỏi đáp"
                >
                  ⚠ chưa embed
                </span>
              )
            )}
          </div>

          {/* Câu hỏi — font lớn rõ hơn */}
          <div
            className="rich-content text-[15px] font-semibold text-slate-900 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderRichText(q.question) }}
          />

          {/* Đáp án preview */}
          <div
            className={`rich-content mt-1.5 text-sm text-slate-600 leading-relaxed ${expanded ? '' : 'line-clamp-2'}`}
            dangerouslySetInnerHTML={{ __html: renderRichText(q.answer) }}
          />
          {stripHtml(q.answer).length > 120 && (
            <button onClick={() => setExpanded((v) => !v)}
                    className="text-xs text-emerald-600 hover:underline mt-1 font-medium">
              {expanded ? '↑ Thu gọn' : '↓ Xem đầy đủ'}
            </button>
          )}

          {/* Footer meta gọn — nguồn + NV hỏi (M63) + ngày */}
          <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400 flex-wrap">
            {q.source && q.source !== 'manual' && (
              <>
                <span title="Nguồn câu hỏi">
                  {q.source === 'ai_generated' ? '🤖 AI tạo' : q.source === 'imported' ? '📥 Import' : `nguồn: ${q.source}`}
                </span>
                <span>·</span>
              </>
            )}
            {/* M63 (2026-04-25): NV đã hỏi câu này — CHỈ hiển thị ở bộ "Chờ duyệt"
                (giúp admin có ngữ cảnh khi duyệt). Sau khi duyệt vào bộ chính thức,
                câu là kiến thức chung → không cần track NV nữa. Data DB vẫn giữ. */}
            {isChoduyet && q.asked_by_employee_name && (
              <>
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 font-medium"
                  title={`Câu hỏi này được nhân viên "${q.asked_by_employee_name}" hỏi`}
                >
                  👤 {q.asked_by_employee_name}
                </span>
                <span>·</span>
              </>
            )}
            <span title={q.created_at}>📅 {formatTime(q.created_at)}</span>
          </div>
        </div>

        {/* Actions group bên phải */}
        <div className="flex-shrink-0 flex items-center gap-1">
          {isChoduyet && (
            <>
              <button onClick={onApprove}
                      title="Duyệt → AI ngon"
                      className="px-2.5 py-1 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 font-medium">
                ✓ Duyệt
              </button>
              <button onClick={onApproveEdit}
                      title="Sửa + Duyệt → AI đã sửa"
                      className="px-2.5 py-1 text-xs bg-amber-500 text-white rounded-md hover:bg-amber-600 font-medium">
                ✏ Sửa + Duyệt
              </button>
              <button onClick={onReject}
                      title="Từ chối (xoá)"
                      className="px-2.5 py-1 text-xs bg-rose-600 text-white rounded-md hover:bg-rose-700 font-medium">
                ✕ Từ chối
              </button>
              <span className="mx-1 text-slate-300">|</span>
            </>
          )}

          <button onClick={onEdit}
                  title="Sửa câu hỏi này"
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-slate-300 rounded-md hover:bg-slate-100 font-medium">
            ✏ Sửa
          </button>
          <button onClick={onMoveBo}
                  title="Chuyển sang bộ khác"
                  className="px-2 py-1 text-xs border border-slate-300 rounded-md hover:bg-slate-100">
            📦
          </button>
          <button onClick={onDelete}
                  title="Xoá câu hỏi (không hoàn tác)"
                  className="px-2 py-1 text-xs border border-rose-300 text-rose-700 rounded-md hover:bg-rose-50">
            🗑
          </button>
        </div>
      </div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN') + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

/**
 * M30.A (2026-04-24) — AiGenerateDialog (PRD R-P0-B4a)
 *
 * Admin nhập chủ đề + chọn 5/10/20 câu + optional seed text → BE RAG pipeline
 * tạo câu mới → insert vào bo_target (default cho_duyet để admin duyệt).
 */
function AiGenerateDialog({ onClose, onGenerated }) {
  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(10);
  const [seedText, setSeedText] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);  // kết quả trả về

  async function submit(e) {
    e?.preventDefault?.();
    if (topic.trim().length < 3) {
      toast.error('Chủ đề phải ≥ 3 ký tự');
      return;
    }
    setBusy(true);
    try {
      const res = await api.questionsAiGenerate({
        topic: topic.trim(),
        count,
        seed_text: seedText.trim() || undefined,
      });
      setPreview(res);
      // Không đóng ngay — admin xem preview, bấm "Xong" để close
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'AI generate thất bại');
    } finally {
      setBusy(false);
    }
  }

  function done() {
    onGenerated(preview);
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-semibold text-lg bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            🪄 AI tạo câu hỏi mới (RAG)
          </h3>
          <button onClick={onClose} disabled={busy} className="text-slate-500 hover:text-slate-800 text-xl leading-none">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!preview && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Chủ đề <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="VD: Quy trình kiểm tra sản phẩm mẹ-bé"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  disabled={busy}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Số câu cần tạo (1 - 20)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    max={20}
                    step={1}
                    value={count}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isFinite(n)) setCount(1);
                      else setCount(Math.max(1, Math.min(20, n)));
                    }}
                    className="w-24 px-3 py-2 border rounded-lg text-center font-semibold"
                    disabled={busy}
                  />
                  <span className="text-sm text-slate-500">Nhanh:</span>
                  {[5, 10].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setCount(n)}
                      disabled={busy}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                        count === n
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <span className="text-xs text-slate-500 ml-auto">
                    ~{count * 100}đ (ước lượng)
                  </span>
                </div>
              </div>

              {/* Ép cứng target = Chờ duyệt — AI tạo không được insert thẳng vào bộ chính */}
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-sky-900">
                📥 Câu hỏi AI tạo sẽ vào bộ <strong>Chờ duyệt</strong>.
                Bạn review / sửa / approve từng câu → chuyển sang bộ phù hợp.
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Seed text (tuỳ chọn) — AI dùng làm ngữ cảnh bổ sung
                </label>
                <textarea
                  value={seedText}
                  onChange={(e) => setSeedText(e.target.value)}
                  placeholder="Dán tài liệu, ghi chú, batch câu import… (max 5000 ký tự)"
                  rows={4}
                  maxLength={5000}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-xs"
                  disabled={busy}
                />
                <div className="text-[10px] text-slate-400 text-right mt-0.5">{seedText.length}/5000</div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
                <div>💡 AI sẽ retrieve top 15 câu + 5 KB + 3 nội quy tương tự để tránh trùng + đúng fact.</div>
                <div>🔍 Mỗi câu mới được check cosine ≥ 0.88 trùng → bỏ tự động.</div>
              </div>
            </>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="font-semibold text-emerald-900">
                  ✅ Đã tạo {preview.inserted_count} / {preview.generated_count} câu vào <strong>Chờ duyệt</strong>
                </div>
                <div className="text-sm text-emerald-800 mt-1">
                  Chủ đề: <strong>{preview.topic}</strong>
                </div>
                <div className="text-xs text-emerald-700 mt-2 grid grid-cols-2 gap-1">
                  <div>Bỏ {preview.duplicates_skipped} câu trùng</div>
                  <div>Chi phí: {preview.cost.total_vnd}đ ({preview.llm.provider} / {preview.llm.model})</div>
                  <div>Tokens: {preview.llm.tokens_in} in / {preview.llm.tokens_out} out</div>
                  <div>Thời gian: {preview.latency_ms}ms</div>
                </div>
              </div>

              {preview.duplicates?.length > 0 && (
                <details className="border border-amber-200 rounded-lg p-3 bg-amber-50">
                  <summary className="text-sm font-medium text-amber-900 cursor-pointer">
                    🔁 {preview.duplicates.length} câu bỏ vì trùng ≥ 88%
                  </summary>
                  <ul className="mt-2 space-y-1 text-xs text-amber-800">
                    {preview.duplicates.map((d, i) => (
                      <li key={i}>
                        "{d.question}" — trùng #{d.dup_id} ({Math.round(d.score * 100)}%)
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="text-xs text-slate-500 italic">
                Bấm "Xong" → tự chuyển sang tab Chờ duyệt để review.
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-slate-50 flex gap-2 justify-end">
          {!preview ? (
            <>
              <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-sm">
                Huỷ
              </button>
              <button
                onClick={submit}
                disabled={busy || topic.trim().length < 3}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {busy ? `⏳ Đang tạo ${count} câu…` : `🪄 Tạo ${count} câu hỏi`}
              </button>
            </>
          ) : (
            <button
              onClick={done}
              className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            >
              Xong
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * M29.A2 (2026-04-24) — DuplicatesScanDialog (PRD R-P0-B4 "AI Kiểm tra câu hỏi trùng")
 *
 * Hiển thị list các cặp câu có cosine ≥ threshold (default 0.88). Admin click
 * 1 pair → đóng scan → mở MergeDialog với 2 câu đó pre-selected.
 *
 * Data shape: { loading, pairs: [{q1, q2, score}], total_scanned, total_pairs_found, threshold, note }
 */
function DuplicatesScanDialog({ bo, data, onClose, onPickPair }) {
  const {
    loading, pairs = [], total_scanned = 0, total_pairs_found = 0,
    threshold = 0.88, note,
    embed_cost_vnd = 0, embed_count = 0,  // M120
  } = data || {};

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[85vh] flex flex-col">
        <div className="p-5 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg text-amber-700">
              🔍 Cặp câu trùng lặp trong bộ <span className="font-mono text-sm">{bo}</span>
            </h3>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-800 text-xl leading-none"
              title="Đóng"
            >✕</button>
          </div>
          {!loading && (
            <div className="text-xs text-slate-500 mt-1">
              Đã quét {total_scanned} câu · Tìm thấy {total_pairs_found} cặp có cosine ≥ {(threshold * 100).toFixed(0)}%
              {pairs.length < total_pairs_found && ` (hiển thị ${pairs.length} cặp cao nhất)`}
              {embed_count > 0 && (
                <span className="ml-1 text-amber-600">
                  · embed tạm {embed_count} câu (~{embed_cost_vnd.toLocaleString('vi-VN')}đ)
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="text-center py-16 text-slate-500">
              <div className="text-3xl mb-2">⏳</div>
              Đang quét pairwise cosine similarity…
            </div>
          )}
          {!loading && note && (
            <div className="text-center py-16 text-slate-500 italic">
              {note}
            </div>
          )}
          {!loading && !note && pairs.length === 0 && (
            <div className="text-center py-16 text-slate-500">
              <div className="text-3xl mb-2">✨</div>
              Không tìm thấy cặp trùng nào với ngưỡng {(threshold * 100).toFixed(0)}% — bộ này khá sạch.
            </div>
          )}
          {!loading && pairs.length > 0 && (
            <div className="space-y-3">
              {pairs.map((pair, i) => {
                const pct = Math.round(pair.score * 100);
                const severity =
                  pct >= 95 ? { bg: 'bg-rose-50', border: 'border-rose-300', badge: 'bg-rose-600' } :
                  pct >= 90 ? { bg: 'bg-orange-50', border: 'border-orange-300', badge: 'bg-orange-500' } :
                              { bg: 'bg-amber-50', border: 'border-amber-300', badge: 'bg-amber-500' };
                return (
                  <button
                    key={`${pair.q1.id}-${pair.q2.id}`}
                    onClick={() => onPickPair(pair)}
                    className={`w-full text-left border-2 rounded-xl p-3 hover:shadow-md transition ${severity.bg} ${severity.border}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-slate-500 font-mono">#{i + 1}</span>
                      <span className={`text-xs font-bold text-white px-2 py-0.5 rounded-full ${severity.badge}`}>
                        {pct}% trùng
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white rounded p-2 border border-slate-200">
                        <div className="text-[10px] text-slate-400 mb-1">#{pair.q1.id}</div>
                        <div className="font-medium text-slate-800 line-clamp-2">{pair.q1.question}</div>
                        <div className="text-xs text-slate-500 line-clamp-2 mt-1">{pair.q1.answer}</div>
                      </div>
                      <div className="bg-white rounded p-2 border border-slate-200">
                        <div className="text-[10px] text-slate-400 mb-1">#{pair.q2.id}</div>
                        <div className="font-medium text-slate-800 line-clamp-2">{pair.q2.question}</div>
                        <div className="text-xs text-slate-500 line-clamp-2 mt-1">{pair.q2.answer}</div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-2 text-center">
                      → Click để xem chi tiết & chọn câu giữ
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-3 border-t bg-slate-50 flex items-center justify-between text-xs text-slate-500">
          <span>💡 Ngưỡng {(threshold * 100).toFixed(0)}% là mặc định theo PRD R-P0-F5 duplicate detection</span>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded bg-slate-200 hover:bg-slate-300"
          >Đóng</button>
        </div>
      </div>
    </div>
  );
}

/**
 * M29.A (2026-04-24) — MergeDialog (PRD R-P0-B15)
 *
 * Hiển thị 2 câu cạnh nhau, admin chọn câu **giữ lại**. Câu còn lại bị
 * hard delete qua DELETE /questions/:id (đã có sẵn + xử FK từ M23 fix).
 *
 * Use case: admin phát hiện 2 câu trùng nghĩa trong cùng bộ hoặc khác bộ
 * → chọn 2 câu → xác nhận giữ câu tốt hơn → câu còn lại biến mất.
 */
function MergeDialog({ q1, q2, onCancel, onConfirm }) {
  const [keepId, setKeepId] = useState(q1.id);
  const [busy, setBusy] = useState(false);

  const keepQ = keepId === q1.id ? q1 : q2;
  const deleteQ = keepId === q1.id ? q2 : q1;

  async function handleConfirm() {
    setBusy(true);
    try {
      await onConfirm(keepId, deleteQ.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full p-6 space-y-4">
        <h3 className="font-semibold text-lg text-rose-700">🔀 Gộp 2 câu trùng</h3>
        <div className="text-sm text-slate-600">
          Chọn câu <strong>giữ lại</strong>. Câu còn lại sẽ bị <strong className="text-rose-700">xoá vĩnh viễn</strong> (hard delete).
        </div>

        <div className="grid grid-cols-2 gap-4">
          {[q1, q2].map((q) => (
            <label
              key={q.id}
              className={`border-2 rounded-xl p-4 cursor-pointer transition ${
                keepId === q.id
                  ? 'border-emerald-400 bg-emerald-50'
                  : 'border-slate-200 hover:border-slate-300 bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="radio"
                  name="keepId"
                  checked={keepId === q.id}
                  onChange={() => setKeepId(q.id)}
                  className="w-4 h-4"
                />
                <span className={`text-xs font-semibold ${keepId === q.id ? 'text-emerald-700' : 'text-slate-500'}`}>
                  {keepId === q.id ? '✓ GIỮ LẠI' : '✕ Sẽ xoá'}
                </span>
                <span className="text-xs text-slate-400 ml-auto">#{q.id} · {q.bo}</span>
              </div>
              {q.topic && (
                <div className="text-xs text-slate-500 mb-1">Chủ đề: {q.topic}</div>
              )}
              <div className="rich-content font-medium text-slate-900 text-sm mb-1"
                   dangerouslySetInnerHTML={{ __html: renderRichText(q.question) }} />
              <div className="rich-content text-xs text-slate-600 line-clamp-4"
                   dangerouslySetInnerHTML={{ __html: renderRichText(q.answer) }} />
            </label>
          ))}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
          ⚠️ Câu <strong>#{deleteQ.id}</strong> (<em>{(deleteQ.question || '').slice(0, 60)}…</em>) sẽ bị xoá vĩnh viễn. Audit log sẽ ghi lại nội dung để tra cứu.
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm"
          >
            Huỷ
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
          >
            {busy ? 'Đang gộp…' : `Gộp: giữ #${keepQ.id}, xoá #${deleteQ.id}`}
          </button>
        </div>
      </div>
    </div>
  );
}
